/* eslint-disable react-hooks/exhaustive-deps */
import React, {useMemo, useState} from "react"
import moment from "moment"
import {useTranslation} from "react-i18next"
import {useMutation, useQuery} from "@tanstack/react-query"
import {Col, Row} from "antd"
import {BaseButton} from "components/buttons/Button/BaseButton"
import {FormLabel} from "components/Form"
import {KlassDropdown} from "components/Select"
import ProgramSelect from "components/ProgramSelect"
import ProgramVersionSelect from "components/ProgramVersionSelect"
import {NewStudentStatusSelectFilter} from "components/NewStudentStatusSelect"
import {BaseRangePicker} from "components/DateTimePicker"
import CampusSelect from "components/CampusSelect"
import {BaseInput} from "components/inputs"
import {ExportButton, ExportFileType} from "components/ui"
import {KlassappTable, KlassappTableHeader, BaseNewFilter} from "uiKit"
import {KlassappTableProps, Order} from "types/common"
import {FilterKey} from "types/filter"
import {StudentStatus} from "types/students"
import {exportCsv, exportExcel} from "helpers/export-table"
import {formatDateOnly, handleError, toastInfo} from "helpers"
import {useModel} from "hooks"
import {useFilterStorage} from "hooks/useFilterStorage"
import {reportsService, sapCheckpointsService} from "services"
import {KlassappTableHOC} from "HOC"
import {sapCheckpointKindOptions, SapCheckpointKind} from "types/settings/sap-checkpoint"
import {ListSapCheckpointChecksPayload, SapCheckpointCheck} from "types/settings/sap-checkpoint-check"
import RunSapChecksPopup from "./RunSapChecksPopup"
import SapChecksStats from "./SapChecksStats"
import styles from "./SapChecksList.module.css"

type SapChecksListFilter = {
    programs: Array<{id: number}>
    programVersions: Array<{id: number}>
    sapKinds: Array<{id: SapCheckpointKind; name: string}>
    studentStatuses: StudentStatus[]
    campuses: Array<{id: number}>
    dateHitFrom: string | null
    dateHitTo: string | null
    checkpointHoursFrom: string
    checkpointHoursTo: string
}

const DEFAULT_FILTER: SapChecksListFilter = {
    programs: [],
    programVersions: [],
    sapKinds: [],
    studentStatuses: [],
    campuses: [],
    dateHitFrom: null,
    dateHitTo: null,
    checkpointHoursFrom: "",
    checkpointHoursTo: ""
}

function formatHours(value: number | null | undefined) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return ""
    }
    return String(Number(value))
}

function formatCheckedAt(value: string | null | undefined, dateFormat: string) {
    if (!value) {
        return ""
    }
    return moment(value).format(`${dateFormat} hh:mm A`)
}

function displayValue(value: string | number | null | undefined) {
    if (value === null || value === undefined || value === "") {
        return ""
    }
    return String(value)
}

function idsOf(items: Array<{id: number}> | undefined) {
    return (items ?? []).map((item) => item.id).filter((id) => Number.isFinite(id))
}

function parseHoursBound(value: string | undefined) {
    if (value == null || value.trim() === "") {
        return null
    }
    const hours = Number(value)
    return Number.isFinite(hours) ? hours : null
}

function toListSort(orderField?: {field?: string; order?: string}): {
    orderBy: string
    orderDir: "ASC" | "DESC"
} {
    return {
        orderBy: orderField?.field || "lastName",
        orderDir: orderField?.order === Order.Desc ? "DESC" : "ASC"
    }
}

type SapChecksListProps = KlassappTableProps & {
    isReport?: boolean
}

function SapChecksList(props: SapChecksListProps) {
    const {isReport = false} = props
    const {
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        isShowTableHeaderAction,
        tableHeaderActions,
        orderField,
        dispatch,
        dispatchFunc,
        tableRef,
        onChangePage,
        onChangeRowPerPage,
        onChangeFields,
        onChangeAllFields,
        onDraggableColumn,
        onUpdateRowData,
        onUpdateTableData,
        onClickSortColumn,
        isLoadedTableFuncs
    } = props
    const {t} = useTranslation(["academics", "user"])
    const model = useModel()
    const dateFormat = model.getUserDateFormat()
    const [rerunningId, setRerunningId] = useState<number | null>(null)
    const [isExporting, setIsExporting] = useState(false)
    const [isRunSapPopupVisible, setIsRunSapPopupVisible] = useState(false)
    const {searchValue, currentFilter, setCurrentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        isReport ? FilterKey.SapChecksReport : FilterKey.SapChecks,
        DEFAULT_FILTER
    )
    const [appliedFilter, setAppliedFilter] = useState<SapChecksListFilter>(currentFilter)
    const [appliedSearch, setAppliedSearch] = useState(searchValue || "")

    const sapKindOptions = useMemo(
        () =>
            sapCheckpointKindOptions.map((kind) => ({
                id: kind.id,
                name: t(`academics.sapChecks.kind.${kind.id}`)
            })),
        [t]
    )

    const getSapKindLabel = (sapKind: SapCheckpointCheck["sapKind"]) => {
        const option = sapKindOptions.find((kind) => kind.id === sapKind)
        return option ? option.name : sapKind
    }

    const getStatusLabel = (item: SapCheckpointCheck) => {
        const statusCode = item.jsonData?.status
        if (!statusCode) {
            return ""
        }
        const statusLabel = t(`academics.sapChecks.sapStatus.${statusCode}`, {defaultValue: statusCode})
        if (item.sapKind === SapCheckpointKind.FA) {
            return t("academics.sapChecks.titleIvStatus", {status: statusLabel})
        }
        return statusLabel
    }

    const decorateItem = (item: SapCheckpointCheck) => {
        const versionName = item.programVersion?.versionName
        const versionCode = item.programVersion?.versionCode
        const programVersion = versionCode ? `${versionName} (${versionCode})` : versionName
        return {
            ...item,
            id: item.sapCheckpointCheckId,
            firstName: item.student?.firstName || "",
            lastName: item.student?.lastName || "",
            recordTypeLabel: item.isRerun ? t("academics.sapChecks.rerunRecord") : t("academics.sapChecks.check"),
            sapKindLabel: getSapKindLabel(item.sapKind),
            incrementNumberLabel: displayValue(item.jsonData?.incrementNumber || null),
            dateRunLabel: formatDateOnly(item.jsonData?.dateRun, dateFormat),
            dateHitLabel: formatDateOnly(item.jsonData?.dateHit, dateFormat),
            statusLabel: getStatusLabel(item),
            attendancePercentageLabel: displayValue(item.jsonData?.attendancePercentage),
            gradeLabel: displayValue(item.jsonData?.grade),
            programName: item.program?.name || "",
            programVersionName: programVersion || "",
            shiftNames: (item.shifts ?? [])
                .map((shift) => shift.name)
                .filter(Boolean)
                .join(", "),
            checkpointHoursLabel: formatHours(item.checkpointHours),
            completedHoursLabel: formatHours(item.completedHours),
            checkedAtLabel: formatCheckedAt(item.checkedAt, dateFormat),
            rerunResultLabel: item.rerunResult ? t(`academics.sapChecks.result.${item.rerunResult}`) : "",
            customUserId: item.customUserId || "",
            enrollmentId: item.enrollmentId || "",
            startingTermName: item.startingTermName || "",
            studentStatusName: item.studentStatusName || "",
            campusNames: (item.campusNames ?? []).filter(Boolean).join(", "),
            currentCompletedHoursLabel: formatHours(item.currentCompletedHours),
            ldaLabel: formatDateOnly(item.lda, dateFormat)
        }
    }

    const buildFilter = (filter: SapChecksListFilter, search = ""): ListSapCheckpointChecksPayload["filter"] => {
        const programIds = idsOf(filter.programs)
        const programVersionIds = idsOf(filter.programVersions)
        const sapKinds = (filter.sapKinds ?? []).map((kind) => kind.id)
        const studentStatusIds = (filter.studentStatuses ?? [])
            .map((status) => status.statusId)
            .filter((id) => Number.isFinite(id))
        const campusIds = idsOf(filter.campuses)
        const checkpointHoursFrom = parseHoursBound(filter.checkpointHoursFrom)
        const checkpointHoursTo = parseHoursBound(filter.checkpointHoursTo)
        const term = search.trim()
        return {
            ...(programIds.length ? {programIds} : {}),
            ...(programVersionIds.length ? {programVersionIds} : {}),
            ...(sapKinds.length ? {sapKinds} : {}),
            ...(studentStatusIds.length ? {studentStatusIds} : {}),
            ...(campusIds.length ? {campusIds} : {}),
            ...(filter.dateHitFrom ? {dateHitFrom: filter.dateHitFrom} : {}),
            ...(filter.dateHitTo ? {dateHitTo: filter.dateHitTo} : {}),
            ...(checkpointHoursFrom == null ? {} : {checkpointHoursFrom}),
            ...(checkpointHoursTo == null ? {} : {checkpointHoursTo}),
            ...(term ? {search: term} : {})
        }
    }

    const listSort = toListSort(orderField)

    const checksQuery = useQuery(
        ["sap-checkpoint-checks-all", isReport, page, pageSize, appliedFilter, appliedSearch, listSort],
        async () => {
            const payload = {
                filter: buildFilter(appliedFilter, appliedSearch),
                range: {page, pageSize},
                sort: listSort,
                linkedEntities: true
            }
            const {data: checks, total: checksTotal} = await sapCheckpointsService.listChecks(payload)
            return {data: checks.map(decorateItem), total: checksTotal}
        },
        {
            enabled: isLoadedTableFuncs,
            keepPreviousData: true,
            onSuccess: (result) => {
                dispatch({data: result.data, total: result.total})
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    const rerunMutation = useMutation(
        (sapCheckpointCheckId: number) => sapCheckpointsService.rerunCheck(sapCheckpointCheckId),
        {
            onSuccess: () => {
                checksQuery.refetch()
            },
            onError: (error) => {
                handleError(error)
            },
            onSettled: () => {
                setRerunningId(null)
            }
        }
    )

    const onRerun = (sapCheckpointCheckId: number) => {
        if (rerunMutation.isLoading) {
            return
        }
        setRerunningId(sapCheckpointCheckId)
        rerunMutation.mutate(sapCheckpointCheckId)
    }

    const appliedChecksFilter = buildFilter(appliedFilter)
    const listFilter = buildFilter(appliedFilter, appliedSearch)

    const onClickExportBtn = async (type: ExportFileType) => {
        try {
            setIsExporting(true)
            const reportColumns = columns.filter(
                (column) => fields.includes(column.title) && column.field !== "rerunHtml"
            )
            if (isReport && total > 1000) {
                await reportsService.generateReport({
                    reportKey: "sapChecksReport",
                    payload: {
                        filter: listFilter,
                        fields: reportColumns.map((column) => ({
                            field: column.field,
                            title: column.title
                        }))
                    },
                    format: type
                })
                toastInfo(t("academics.sapChecks.reportQueued"))
                return
            }
            const exportPayload = {
                filter: listFilter,
                range: {page: 1, pageSize: total},
                sort: listSort,
                linkedEntities: true
            }
            const checks = total ? (await sapCheckpointsService.listChecks(exportPayload)).data : []
            const payload = {
                filename: `${isReport ? "Sap-Checks-Report" : "Sap-Checks"}-${moment().format("MM_DD_YYYY")}`,
                columns: reportColumns.map((column) => column.title.toUpperCase()),
                rows: checks.map((item) => {
                    const decorated = decorateItem(item) as Record<string, unknown>
                    return reportColumns.map((column) => {
                        const value = decorated[column.field]
                        return value == null ? "" : String(value).replace(/\r\n|\n|\r/g, " ")
                    })
                })
            }
            if (type === "csv") {
                exportCsv(payload)
            } else if (type === "excel") {
                exportExcel(payload)
            }
        } catch (error) {
            handleError(error)
        } finally {
            setIsExporting(false)
        }
    }

    const getFields = () => [
        t("user:firstName"),
        t("user:lastName"),
        t("user:userId"),
        t("academics.sapChecks.enrollmentId"),
        t("academics.sapChecks.startingTerm"),
        t("academics.sapChecks.filters.studentStatus"),
        t("academics.sapChecks.campus"),
        t("academics.sapChecks.program"),
        t("academics.sapChecks.programVersion"),
        t("academics.sapChecks.shifts"),
        t("academics.sapChecks.currentCompletedHours"),
        t("academics.sapChecks.lda"),
        t("academics.sapChecks.recordType"),
        t("academics.sapChecks.sapKind"),
        t("academics.sapChecks.status"),
        t("academics.sapChecks.attendancePercentage"),
        t("academics.sapChecks.grade"),
        t("academics.sapChecks.incrementNumber"),
        t("academics.sapChecks.dateRun"),
        t("academics.sapChecks.dateHit"),
        t("academics.sapChecks.checkpointHours"),
        t("academics.sapChecks.completedHours"),
        t("academics.sapChecks.checkedAt"),
        t("academics.sapChecks.resultTitle"),
        t("academics.sapChecks.rerun")
    ]

    const getFilterMemoryKey = () => {
        return isReport ? FilterKey.SapChecksReport : FilterKey.SapChecks
    }

    const getColumns = () => [
        {
            title: t("user:firstName"),
            field: "firstName",
            columnIndex: 1,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("user:lastName"),
            field: "lastName",
            columnIndex: 2,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("user:userId"),
            field: "customUserId",
            columnIndex: 3,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.enrollmentId"),
            field: "enrollmentId",
            columnIndex: 4,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.startingTerm"),
            field: "startingTermName",
            columnIndex: 5,
            sortable: true,
            style: {minWidth: "150px"}
        },
        {
            title: t("academics.sapChecks.filters.studentStatus"),
            field: "studentStatusName",
            columnIndex: 6,
            sortable: true,
            style: {minWidth: "150px"}
        },
        {
            title: t("academics.sapChecks.campus"),
            field: "campusNames",
            columnIndex: 7,
            sortable: true,
            style: {minWidth: "160px"}
        },
        {
            title: t("academics.sapChecks.program"),
            field: "programName",
            columnIndex: 8,
            sortable: true,
            style: {minWidth: "160px"}
        },
        {
            title: t("academics.sapChecks.programVersion"),
            field: "programVersionName",
            columnIndex: 9,
            sortable: true,
            style: {minWidth: "180px"}
        },
        {
            title: t("academics.sapChecks.shifts"),
            field: "shiftNames",
            columnIndex: 10,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.currentCompletedHours"),
            field: "currentCompletedHoursLabel",
            columnIndex: 11,
            orderField: "currentCompletedHours",
            sortable: true,
            style: {minWidth: "180px"}
        },
        {
            title: t("academics.sapChecks.lda"),
            field: "ldaLabel",
            columnIndex: 12,
            orderField: "lda",
            sortable: true,
            style: {minWidth: "130px"}
        },
        {
            title: t("academics.sapChecks.recordType"),
            field: "recordTypeLabel",
            columnIndex: 13,
            orderField: "recordType",
            sortable: true,
            style: {minWidth: "120px"}
        },
        {
            title: t("academics.sapChecks.sapKind"),
            field: "sapKindLabel",
            columnIndex: 14,
            orderField: "sapKind",
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.status"),
            field: "statusLabel",
            columnIndex: 15,
            orderField: "status",
            sortable: true,
            style: {minWidth: "160px"}
        },
        {
            title: t("academics.sapChecks.attendancePercentage"),
            field: "attendancePercentageLabel",
            columnIndex: 16,
            orderField: "attendancePercentage",
            sortable: true,
            style: {minWidth: "130px"}
        },
        {
            title: t("academics.sapChecks.grade"),
            field: "gradeLabel",
            columnIndex: 17,
            orderField: "grade",
            sortable: true,
            style: {minWidth: "100px"}
        },
        {
            title: t("academics.sapChecks.incrementNumber"),
            field: "incrementNumberLabel",
            columnIndex: 18,
            orderField: "incrementNumber",
            sortable: true,
            style: {minWidth: "110px"}
        },
        {
            title: t("academics.sapChecks.dateRun"),
            field: "dateRunLabel",
            columnIndex: 19,
            orderField: "dateRun",
            sortable: true,
            style: {minWidth: "120px"}
        },
        {
            title: t("academics.sapChecks.dateHit"),
            field: "dateHitLabel",
            columnIndex: 20,
            orderField: "dateHit",
            sortable: true,
            style: {minWidth: "120px"}
        },
        {
            title: t("academics.sapChecks.checkpointHours"),
            field: "checkpointHoursLabel",
            columnIndex: 21,
            orderField: "checkpointHours",
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.completedHours"),
            field: "completedHoursLabel",
            columnIndex: 22,
            orderField: "completedHours",
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.checkedAt"),
            field: "checkedAtLabel",
            columnIndex: 23,
            orderField: "checkedAt",
            sortable: true,
            style: {minWidth: "170px"}
        },
        {
            title: t("academics.sapChecks.resultTitle"),
            field: "rerunResultLabel",
            columnIndex: 24,
            orderField: "rerunResult",
            sortable: true,
            style: {minWidth: "160px"}
        },
        {
            title: t("academics.sapChecks.rerun"),
            field: "rerunHtml",
            columnIndex: 25,
            style: {minWidth: "120px"}
        }
    ]

    React.useEffect(() => {
        dispatch({isClassComponent: false, orderField: {field: "lastName", order: Order.Asc}})
        dispatchFunc([
            {
                key: "getPageTitle",
                func: () => t(isReport ? "academics.sapChecks.reportTitle" : "academics.sapChecks.tab")
            },
            {key: "getFilterMemoryKey", func: getFilterMemoryKey},
            {key: "getListData", func: checksQuery.refetch},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [])

    const onChangeFilter = <Key extends keyof SapChecksListFilter>(key: Key, value: SapChecksListFilter[Key]) => {
        const newFilter: SapChecksListFilter = {
            ...DEFAULT_FILTER,
            ...currentFilter,
            [key]: value
        }
        if (key === "programs") {
            newFilter.programVersions = []
        }
        setCurrentFilter(newFilter)
    }

    const onApplyFilter = () => {
        const nextFilter: SapChecksListFilter = {...DEFAULT_FILTER, ...currentFilter}
        applyFilter()
        setAppliedFilter(nextFilter)
        dispatch({page: 1})
    }

    const onSearchInput = (value: string) => {
        changeSearch(value)
        setAppliedSearch(value || "")
        dispatch({page: 1})
    }

    const programIds = idsOf(currentFilter.programs)
    const rows = (data ?? []).map((item) => ({
        ...item,
        rerunHtml:
            item.rerunCount > 0 ? (
                <span>{t("academics.sapChecks.alreadyRerun")}</span>
            ) : (
                <div className={styles.action}>
                    <BaseButton
                        title={t("academics.sapChecks.rerun")}
                        variant="secondary"
                        uppercase={false}
                        className={styles.rerunButton}
                        loading={rerunningId === item.sapCheckpointCheckId}
                        disabled={rerunMutation.isLoading}
                        onClick={(event) => {
                            event.stopPropagation()
                            onRerun(item.sapCheckpointCheckId)
                        }}
                    />
                </div>
            )
    }))

    return (
        <div>
            {isReport ? null : <SapChecksStats filter={appliedChecksFilter} />}
            <BaseNewFilter
                filter={currentFilter}
                onClick={onApplyFilter}
                onClickClear={clearFilter}
                searchValue={searchValue}
                onSearchInput={onSearchInput}
                renderRightFilter={() => (
                    <div className={styles.actionWrap}>
                        {isReport ? null : (
                            <BaseButton
                                title={t("academics.sapChecks.runSap")}
                                onClick={() => setIsRunSapPopupVisible(true)}
                            />
                        )}
                        <ExportButton
                            onSelect={onClickExportBtn}
                            isLoading={isExporting}
                            availableFileTypes={["csv", "excel"]}
                        />
                    </div>
                )}>
                <Row gutter={[12, 12]}>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.programs")} />
                        <ProgramSelect
                            isMulti
                            isClearable
                            placeholder={t("academics.sapChecks.filters.programs")}
                            value={currentFilter.programs}
                            onChange={(programs) => onChangeFilter("programs", programs)}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.programVersions")} />
                        <ProgramVersionSelect
                            isMulti
                            isClearable
                            placeholder={t("academics.sapChecks.filters.programVersions")}
                            programId={programIds.length ? programIds : undefined}
                            value={currentFilter.programVersions}
                            onChange={(programVersions) => onChangeFilter("programVersions", programVersions)}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.sapKinds")} />
                        <KlassDropdown
                            isMulti
                            isClearable
                            options={sapKindOptions}
                            valueKey="id"
                            labelKey="name"
                            placeholder={t("academics.sapChecks.filters.sapKinds")}
                            value={currentFilter.sapKinds}
                            onChange={(sapKinds) => onChangeFilter("sapKinds", sapKinds)}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.studentStatus")} />
                        <NewStudentStatusSelectFilter
                            isMulti
                            isClearable
                            placeholder={t("academics.sapChecks.filters.studentStatus")}
                            value={currentFilter.studentStatuses}
                            onChange={(studentStatuses) => onChangeFilter("studentStatuses", studentStatuses)}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.campuses")} />
                        <CampusSelect
                            isMulti
                            isClearable
                            placeholder={t("academics.sapChecks.filters.campuses")}
                            value={currentFilter.campuses}
                            onChange={(campuses) => onChangeFilter("campuses", campuses ?? [])}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.dateHit")} />
                        <BaseRangePicker
                            dateOnly
                            placeholder={[
                                t("academics.sapChecks.filters.dateFrom"),
                                t("academics.sapChecks.filters.dateTo")
                            ]}
                            value={
                                currentFilter.dateHitFrom || currentFilter.dateHitTo
                                    ? [currentFilter.dateHitFrom, currentFilter.dateHitTo]
                                    : null
                            }
                            onChange={(range) => {
                                setCurrentFilter({
                                    ...DEFAULT_FILTER,
                                    ...currentFilter,
                                    dateHitFrom: range?.[0] || null,
                                    dateHitTo: range?.[1] || null
                                })
                            }}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.checkpointHoursFrom")} />
                        <BaseInput
                            type="number"
                            placeholder={t("academics.sapChecks.filters.checkpointHoursFrom")}
                            value={currentFilter.checkpointHoursFrom}
                            onChange={(checkpointHoursFrom) =>
                                onChangeFilter("checkpointHoursFrom", checkpointHoursFrom)
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.checkpointHoursTo")} />
                        <BaseInput
                            type="number"
                            placeholder={t("academics.sapChecks.filters.checkpointHoursTo")}
                            value={currentFilter.checkpointHoursTo}
                            onChange={(checkpointHoursTo) => onChangeFilter("checkpointHoursTo", checkpointHoursTo)}
                        />
                    </Col>
                </Row>
            </BaseNewFilter>
            <div className={styles.marginTop}>
                <KlassappTableHeader
                    isShowAction={isShowTableHeaderAction}
                    actions={tableHeaderActions}
                    page={page}
                    total={total}
                    defaultPageSize={pageSize}
                    onChangePage={onChangePage}
                    onChangeRowPerPage={onChangeRowPerPage}
                    fields={fields}
                    allFields={allFields}
                    onChangeFields={onChangeFields}
                    onChangeAllFields={onChangeAllFields}
                    onDraggableColumn={onDraggableColumn}
                />
                <KlassappTable
                    columns={columns}
                    data={rows}
                    menuActions={[]}
                    isLoading={checksQuery.isFetching}
                    fields={fields}
                    allFields={allFields}
                    orderField={orderField}
                    tableRef={tableRef}
                    emptyText={t("academics.sapChecks.empty")}
                    onClickRowItem={() => {}}
                    onChangeFields={onChangeFields}
                    onUpdateRowData={onUpdateRowData}
                    onUpdateTableData={onUpdateTableData}
                    onClickSortColumn={onClickSortColumn}
                    onDraggableColumn={onDraggableColumn}
                    onChangeAllFields={onChangeAllFields}
                />
            </div>
            {isReport ? null : (
                <RunSapChecksPopup isVisible={isRunSapPopupVisible} onClose={() => setIsRunSapPopupVisible(false)} />
            )}
        </div>
    )
}

export default KlassappTableHOC(SapChecksList)
